import type {
  CatalogListItem,
  CatalogMarketplaceEntry,
} from '@skupreme/api-contracts/core';
import { MarketplaceAvatar } from '@features/orders';
import { HintLabel } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { MarketplacePriority } from '@utility/constants/catalog';
import { Fragment } from 'react';

/* A list row or a variation: the variation shape leaves some of these out. */
type PricedItem = Partial<
  Pick<
    CatalogListItem,
    'p_price' | 'p_currency' | 'price_rule' | 'is_parent' | 'p_sku'
  >
> & { marketplaces?: Record<string, CatalogMarketplaceEntry> };

function currencyOf(item: PricedItem, regionCurrency?: string) {
  const currency = item.p_currency || regionCurrency || 'USD';

  return Intl.supportedValuesOf('currency').includes(currency)
    ? currency
    : 'USD';
}

/* How far a marketplace price sits from the base: by amount or by percent. */
function difference(price: number, base: number, rule?: string) {
  if (!base || price === base) return null;

  const sign = price > base ? '+' : '−';
  const amount = Math.abs(price - base);

  return rule === 'FIXED'
    ? `${sign}${amount.toFixed(2)}`
    : `${sign}${((100 * amount) / base).toFixed(2)}%`;
}

/** The base price, and what each marketplace charges on hover. */
export function PriceCell({
  item,
  regionCurrency,
}: {
  item: PricedItem;
  regionCurrency?: string;
}) {
  if (item.is_parent) return null;

  const currency = currencyOf(item, regionCurrency);
  const base = item.p_price ?? 0;
  const format = (value: number) =>
    CommonUtility.currencyFormat(value, currency);
  const entries = Object.entries(item.marketplaces ?? {}).sort(
    ([a], [b]) =>
      (MarketplacePriority[a] ?? Number.MAX_SAFE_INTEGER) -
      (MarketplacePriority[b] ?? Number.MAX_SAFE_INTEGER),
  );

  const price = (
    <span className="sk:whitespace-nowrap sk:tabular-nums">{format(base)}</span>
  );

  if (!entries.length) return price;

  return (
    <HintLabel
      placement="bottom start"
      hint={
        <dl className="sk:m-0 sk:grid sk:min-w-56 sk:grid-cols-[1fr_auto_auto] sk:items-center sk:gap-x-3 sk:gap-y-2 sk:text-dense">
          <dt className="sk:text-fg-secondary">Base price</dt>
          <dd className="sk:m-0 sk:text-right sk:tabular-nums">
            {format(base)}
          </dd>
          <dd className="sk:m-0" />
          {entries.map(([key, entry]) => {
            const price = entry.price ?? base;
            const delta = difference(price, base, item.price_rule);

            return (
              <Fragment key={key}>
                <dt className="sk:flex sk:items-center sk:gap-2 sk:text-fg-secondary">
                  <MarketplaceAvatar
                    marketplace={{ ...entry, type: entry.type || key }}
                    fallbackText={key}
                  />
                  {CommonUtility.toCapitalize(entry.type || key)}
                </dt>
                <dd className="sk:m-0 sk:text-right sk:tabular-nums">
                  {format(price)}
                </dd>
                <dd className="sk:m-0 sk:text-right sk:text-xs sk:text-fg-tertiary sk:tabular-nums">
                  {delta}
                </dd>
              </Fragment>
            );
          })}
        </dl>
      }
    >
      {price}
    </HintLabel>
  );
}
